import React, {
  createContext,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import PcBottomSheet, {
  PcBottomSheetProps,
} from '@/components/@IntranetLibrary/PcBottomSheet';

// PC 전역 시트 Provider. 모바일 화면은 이 컨텍스트가 아니라
// @IntranetLibrary/MobileBottomSheet 을 직접 렌더한다.

type BottomSheetConfig = Pick<
  PcBottomSheetProps,
  'title' | 'isExpandable' | 'additionalButton'
>;

// closeAction이 true를 반환하면 닫기를 중단(Prevent Default)하는 패턴
type CloseInterceptor = () => boolean;

type OpenPayload = {
  content: ReactNode;
  onOpen?: () => void;
  onClose?: CloseInterceptor;
} & BottomSheetConfig;

export type BottomSheetContextProps = {
  isOpen: boolean;
  openSheet: (payload: OpenPayload) => void;
  closeSheet: () => void;
  setCloseHandler: (handler: CloseInterceptor) => void;
};
const BottomSheetContext = createContext<BottomSheetContextProps | null>(null);
BottomSheetContext.displayName = 'BottomSheetContext';

const BottomSheetProvider = ({ children }: PropsWithChildren) => {
  const [isOpen, setIsOpen] = useState(false);
  const [sheetConfig, setSheetConfig] = useState<BottomSheetConfig>({
    title: '',
    isExpandable: true,
  });
  const [content, setContent] = useState<ReactNode>(null);
  const closeInterceptorRef = useRef<CloseInterceptor | null>(null);

  const openSheet = useCallback((payload: OpenPayload) => {
    const { content: newContent, onOpen, onClose, ...config } = payload;

    // UI 구성
    setContent(newContent);
    setSheetConfig(config);

    // 핸들러 설정
    closeInterceptorRef.current = onClose || null;

    // Side Effect 실행 (동기적 실행이 필요하다면 여기서)
    if (onOpen) onOpen();

    // 모달 오픈
    setIsOpen(true);
  }, []);

  const closeSheet = useCallback(() => {
    // 닫기 인터셉터 로직: true 반환 시 닫기 중단
    if (closeInterceptorRef.current && closeInterceptorRef.current()) {
      return;
    }

    setIsOpen(false);
  }, []);

  // 동적으로 닫기 핸들러를 교체해야 할 경우 사용
  const setCloseHandler = useCallback((handler: CloseInterceptor) => {
    closeInterceptorRef.current = handler;
  }, []);

  // Context Value Memoization
  const contextValue = useMemo(
    () => ({
      isOpen,
      openSheet,
      closeSheet,
      setCloseHandler,
    }),
    [isOpen, openSheet, closeSheet, setCloseHandler],
  );

  return (
    <BottomSheetContext.Provider value={contextValue}>
      {children}
      <PcBottomSheet isOpen={isOpen} handleClose={closeSheet} {...sheetConfig}>
        {content}
      </PcBottomSheet>
    </BottomSheetContext.Provider>
  );
};

export { BottomSheetProvider, BottomSheetContext };
